import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { LeaderboardListRow } from "@/components/leaderboard/leaderboard-row"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { creatorLeaderboardPath } from "@/routes"
import type { ThisWeekLeaders } from "@/types/crew-hub"

/**
 * OKS-474 — §18 "This Week's Leaders" mini-board (UX Spec §2.4).
 *
 * White card: header + the top rows for the current week, rendered with the
 * SHIPPED `LeaderboardListRow` (TRD KD-3 — reused as-is; it already renders
 * the inline "(you)" suffix via `viewerHighlight` without collapsing the
 * name, so no variant change was needed). When the viewer is outside the
 * visible top rows, their `self_row` is pinned below a separator with the
 * same highlight treatment.
 *
 * Empty: when there are no ranked members this week, the card shows the
 * "no leaders yet" line instead of an empty list.
 *
 * OKS-570 — the header row carries a right-aligned "View full leaderboard"
 * link (→ `creatorLeaderboardPath()`). The full `/leaderboard` page was
 * previously reachable ONLY from the now-removed Rank bottom-tab; this link
 * keeps it reachable. Rendered whether or not the mini-board is empty (the
 * full page has value / the viewer's own row); it follows this section's
 * own visibility on the crew hub (hidden pre-populate is handled upstream).
 */
interface ThisWeekLeadersSectionProps {
  data: ThisWeekLeaders
}

export function ThisWeekLeadersSection({ data }: ThisWeekLeadersSectionProps) {
  const { t } = useTranslation()
  const { leaders, self_row, viewer_membership_id } = data
  const isEmpty = leaders.length === 0

  return (
    <section aria-labelledby="this-week-leaders-heading">
      <Card>
        <CardContent className="flex flex-col gap-2 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <h2
              id="this-week-leaders-heading"
              className="text-muted-foreground text-xs font-semibold tracking-wide uppercase"
            >
              {t("crew_hub.leaders.title")}
            </h2>
            <Link
              href={creatorLeaderboardPath()}
              data-testid="this-week-leaders-view-all"
              aria-label={t("crew_hub.leaders.view_all")}
              className="text-primary text-sm font-medium hover:underline"
            >
              {t("crew_hub.leaders.view_all")}
            </Link>
          </div>

          {isEmpty ? (
            <p
              data-testid="this-week-leaders-empty"
              className="text-muted-foreground py-2 text-sm"
            >
              {t("crew_hub.leaders.empty")}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {leaders.map((row) => (
                <LeaderboardListRow
                  key={row.membership_id}
                  row={row}
                  viewerHighlight={row.membership_id === viewer_membership_id}
                />
              ))}
            </ul>
          )}

          {self_row && (
            <>
              <Separator className="my-1" />
              <ul
                className="flex flex-col"
                data-testid="this-week-leaders-self-pin"
              >
                <LeaderboardListRow row={self_row} viewerHighlight />
              </ul>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
